import type {
  Agreement,
  Client,
  Invoice,
  OrganizationProfile,
} from '@hero/schema';

/**
 * Completeness predicates for the financing apply form's
 * "requirement gates". Plain pure functions so the same logic
 * can power:
 *  - The card status badges (✓ / ! / locked).
 *  - The submit hard-block check.
 *  - Any future server-side echo (e.g. lender packet builder).
 *
 * Keep the rules explicit and field-by-field rather than collapsing
 * into a single boolean blob — the apply form lists exactly what's
 * missing to the user, so we need to know *which* field failed, not
 * just that *something* did.
 */

/* -------------------------------------------------------------- */
/* Carrier (current Org) — must be fully complete to apply       */
/* -------------------------------------------------------------- */

export interface CarrierLegalChecks {
  registrationName: boolean;
  registrationId: boolean;
  registrationImage: boolean;
  taxId: boolean;
  taxImage: boolean;
}

export function carrierLegalChecks(
  profile: OrganizationProfile | undefined,
): CarrierLegalChecks {
  return {
    registrationName: !!profile?.registrationName,
    registrationId: !!profile?.registrationId,
    // `registrationImage` is `imageWithUploadSchema`. A persisted
    // doc has both `id` + `url`; a freshly-uploaded one carries
    // `upload`. Either counts as "present" for the gate.
    registrationImage: hasImage(profile?.registrationImage),
    taxId: !!profile?.taxId,
    taxImage: hasImage(profile?.taxImage),
  };
}

export function isCarrierLegalComplete(
  profile: OrganizationProfile | undefined,
): boolean {
  return Object.values(carrierLegalChecks(profile)).every(Boolean);
}

export interface CarrierBankingChecks {
  bankName: boolean;
  bankIBAN: boolean;
  bankAccountName: boolean;
  bankAccountImage: boolean;
}

export function carrierBankingChecks(
  profile: OrganizationProfile | undefined,
): CarrierBankingChecks {
  return {
    bankName: !!profile?.bankName,
    bankIBAN: !!profile?.bankIBAN,
    bankAccountName: !!profile?.bankAccountName,
    // The IBAN-certificate image is required on the bank schema, so
    // a complete bank record always carries one. Treat its absence
    // as the bank section being incomplete.
    bankAccountImage: hasImage(profile?.bankAccountImage),
  };
}

export function isCarrierBankingComplete(
  profile: OrganizationProfile | undefined,
): boolean {
  return Object.values(carrierBankingChecks(profile)).every(Boolean);
}

/* -------------------------------------------------------------- */
/* Client (buyer of the invoice) — split into HARD vs SOFT       */
/* -------------------------------------------------------------- */

/**
 * Hard requirements for the picked Client's legal info — without
 * these the lender can't identify or invoice the counterparty.
 *
 * Soft requirements (CR/Tax document scans) are reported separately
 * — the application can still go out without them, but the lender
 * may discount the offer.
 */
export interface ClientLegalHardChecks {
  registrationName: boolean;
  registrationId: boolean;
  taxId: boolean;
}

export interface ClientLegalSoftChecks {
  registrationImage: boolean;
  taxImage: boolean;
}

export function clientLegalHardChecks(
  client: Client | undefined,
): ClientLegalHardChecks {
  const legal = client?.legal;
  return {
    registrationName: !!legal?.registrationName,
    registrationId: !!legal?.registrationId,
    taxId: !!legal?.taxId,
  };
}

export function clientLegalSoftChecks(
  client: Client | undefined,
): ClientLegalSoftChecks {
  const legal = client?.legal;
  return {
    registrationImage: hasImage(legal?.registrationImage),
    taxImage: hasImage(legal?.taxImage),
  };
}

export function isClientLegalHardComplete(
  client: Client | undefined,
): boolean {
  return Object.values(clientLegalHardChecks(client)).every(Boolean);
}

export function isClientLegalSoftComplete(
  client: Client | undefined,
): boolean {
  return Object.values(clientLegalSoftChecks(client)).every(Boolean);
}

/* -------------------------------------------------------------- */
/* Agreement (between carrier ↔ client) — signed contract required */
/* -------------------------------------------------------------- */

/**
 * Pick the "current" bilateral agreement from a list returned by
 * `useAgreementListApi({ partyId: clientId })`. The list can contain
 * historical agreements in any status; we want the one that's
 * actually in force right now (i.e. `active` or `new`). Older
 * `canceled` / `terminated` / `expired` ones don't satisfy the
 * lender requirement.
 */
export function pickCurrentAgreement(
  agreements: Agreement[] | undefined,
): Agreement | undefined {
  if (!agreements?.length) return undefined;
  return (
    agreements.find(a => a.status === 'active') ??
    agreements.find(a => a.status === 'new')
  );
}

/**
 * The agreement gate is satisfied when:
 *  1. An agreement exists between the carrier and the picked client.
 *  2. The agreement carries a signed-contract PDF (`contractPDF`).
 *
 * Status check is performed via `pickCurrentAgreement`, so callers
 * can pass the raw list from the API hook.
 */
export function isAgreementComplete(
  agreement: Agreement | undefined,
): boolean {
  if (!agreement) return false;
  if (agreement.status !== 'active' && agreement.status !== 'new') return false;
  return hasImage(agreement.contractPDF);
}

/* -------------------------------------------------------------- */
/* Hero invoice (selected from the picker) — proof must be on file */
/* -------------------------------------------------------------- */

/**
 * Field-level checks for a Hero invoice that the seller has
 * picked in the financing apply wizard. Each predicate maps to a
 * line item shown inside the consolidated "Invoice details" card.
 *
 * - `vatFile`       — the seller has attached the VAT PDF
 *                     (`invoice.invoiceProof.image`). Lenders won't
 *                     advance against an invoice they can't see.
 * - `invoiceNumber` — the seller's own accounting reference
 *                     (`invoice.invoiceProof.refNumber`). Hero's
 *                     auto-ref isn't enough — lenders need a number
 *                     that matches the seller's books.
 * - `expectedDueDate` — client expected invoice pay date; stored on
 *                       the Hero invoice as `period.due`. When
 *                       missing the seller must supply it via the
 *                       upload drawer.
 */
export interface HeroInvoiceProofChecks {
  vatFile: boolean;
  invoiceNumber: boolean;
  expectedDueDate: boolean;
}

/**
 * Read the client expected pay date from a Hero invoice. Canonical
 * storage is `Invoice.period.due` — the same value snapshotted as
 * `FinancingApplication.invoice.expectedDueDate` at apply time.
 */
export function getInvoiceExpectedDueDate(
  invoice: Invoice | undefined,
): Date | undefined {
  return invoice?.period?.due ? new Date(invoice.period.due) : undefined;
}

export function heroInvoiceProofChecks(
  invoice: Invoice | undefined,
): HeroInvoiceProofChecks {
  const proof = invoice?.invoiceProof;
  return {
    vatFile: hasImage(proof?.image),
    invoiceNumber: !!proof?.refNumber?.trim(),
    expectedDueDate: !!invoice?.period?.due,
  };
}

export function isHeroInvoiceProofComplete(
  invoice: Invoice | undefined,
): boolean {
  if (!invoice) return false;
  return Object.values(heroInvoiceProofChecks(invoice)).every(Boolean);
}

/* -------------------------------------------------------------- */
/* Roll-up — collected hard blocks for the submit gate           */
/* -------------------------------------------------------------- */

export interface HardBlockSummary {
  /** Hard blocks present today — true === blocked. */
  carrierLegal: boolean;
  carrierBanking: boolean;
  clientLegal: boolean;
  agreement: boolean;
  /**
   * Hero-invoice proof: the seller hasn't completed all three
   * invoice-detail fields (VAT PDF + invoice number + expected pay
   * date) on the picked Hero invoice. Only flagged when an invoice
   * was actually passed in via `heroInvoice` — the field collapses
   * to `false` for the external-tab path.
   */
  heroInvoiceProof: boolean;
  /** Convenience: any hard block at all. */
  any: boolean;
}

export function computeHardBlocks(args: {
  profile: OrganizationProfile | undefined;
  client: Client | undefined;
  agreement: Agreement | undefined;
  /**
   * When the user is on the Hero tab, pass the picked invoice so
   * the proof completeness (VAT PDF + ref + pay date) joins the
   * lender-packet gate. Omit / pass `undefined` for the external
   * tab — there's no Hero-invoice proof to check.
   */
  heroInvoice?: Invoice | undefined;
}): HardBlockSummary {
  const carrierLegal = !isCarrierLegalComplete(args.profile);
  const carrierBanking = !isCarrierBankingComplete(args.profile);
  const clientLegal = !isClientLegalHardComplete(args.client);
  const agreement = !isAgreementComplete(args.agreement);
  // Treat the Hero-invoice-proof gate as satisfied when no
  // invoice was supplied (the external-tab case). The flag only
  // ever turns *true* when a Hero invoice is picked and the
  // seller hasn't filled all three detail fields.
  const heroInvoiceProof = args.heroInvoice
    ? !isHeroInvoiceProofComplete(args.heroInvoice)
    : false;
  return {
    carrierLegal,
    carrierBanking,
    clientLegal,
    agreement,
    heroInvoiceProof,
    any:
      carrierLegal ||
      carrierBanking ||
      clientLegal ||
      agreement ||
      heroInvoiceProof,
  };
}

/* -------------------------------------------------------------- */
/* Internals                                                       */
/* -------------------------------------------------------------- */

/**
 * `imageWithUploadSchema` allows three shapes:
 *   - `{ id, url }`              (persisted, post-upload)
 *   - `{ upload: '<base64|url>' }` (just picked, server hasn't uploaded yet)
 *   - `{ id, url, upload: <new> }` (replacing during edit)
 *
 * For "has the user provided a document?" we count any of those —
 * `upload` is just as much a commitment as a persisted `url`, and
 * the backend will materialise it on save.
 */
function hasImage(
  img:
    | { id?: string; url?: string; upload?: string }
    | undefined
    | null,
): boolean {
  if (!img) return false;
  return !!(img.url || img.upload);
}
